import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { placeholder } from '@/data/catalogImages';
import { useCart } from '@/store/cart';
import { useFavourites } from '@/store/favourites';
import { useAuthStore } from '@/store/auth';
import { api } from '@/lib/api';

export interface Product {
  id: string;
  title: string;
  brand?: string;
  priceGBP: number;
  originalPriceGBP?: number;
  size?: string;
  condition: 'NEW_TAGS' | 'LIKE_NEW' | 'EXCELLENT' | 'GOOD' | 'FAIR';
  imageClass?: string;
  imageUrl?: string;
  verified?: boolean;
  type?: 'SALE' | 'OFFER' | 'SWAP' | 'FREE';
  worn?: string;
  sellerHandle?: string;
  sellerTier?: 'GOLD' | 'VERIFIED' | 'TRUSTED' | 'FAST' | 'CURATED' | null;
  saveCount?: number;
  viewCount?: number;
  co2SavedKg?: number;
  styleTag?: string;
  flashExpiry?: number;
}

const conditionLabel: Record<Product['condition'], string> = {
  NEW_TAGS: 'New with tags', LIKE_NEW: 'Like new', EXCELLENT: 'Excellent', GOOD: 'Good', FAIR: 'Fair',
};
const conditionDots: Record<Product['condition'], number> = {
  NEW_TAGS: 5, LIKE_NEW: 5, EXCELLENT: 4, GOOD: 3, FAIR: 2,
};

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"
      fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={filled ? 0 : 2}
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
    </svg>
  );
}

function useCountdown(expiryMs?: number) {
  const [remaining, setRemaining] = useState(() => expiryMs ? Math.max(0, expiryMs - Date.now()) : 0);
  useEffect(() => {
    if (!expiryMs) return;
    const id = setInterval(() => setRemaining(Math.max(0, expiryMs - Date.now())), 1000);
    return () => clearInterval(id);
  }, [expiryMs]);
  if (!expiryMs || remaining === 0) return null;
  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  return `${h > 0 ? `${h}h ` : ''}${m}m ${s}s`;
}

export function ProductCard({ product }: { product: Product }) {
  const href = `/p/${product.id}`;
  // Only ever show the seller's own uploaded photo; a listing with no photo gets
  // a neutral placeholder, never a stock image that misrepresents the item.
  const cover = product.imageUrl
    ?? placeholder(`${product.brand ?? ''} ${product.title}`.trim(), (product as { category?: string }).category ?? '');
  const [imgSrc, setImgSrc] = useState(cover);
  const [loaded, setLoaded] = useState(false);
  // Contrast-aware discount badge: sample the image region behind it and flip
  // the badge light/dark so it stays legible on any photo background.
  const [badgeOnDark, setBadgeOnDark] = useState(false);
  const addToCart = useCart((s) => s.add);
  const inCart = useCart((s) => s.items.some((i) => i.id === product.id));
  const navigate = useNavigate();
  const toggleFav = useFavourites((s) => s.toggle);
  const saved = useFavourites((s) => s.items.some((i) => i.id === product.id));
  const user = useAuthStore((s) => s.user);
  const showImg = !!imgSrc;

  const discount = product.originalPriceGBP && product.originalPriceGBP > product.priceGBP
    ? Math.round((1 - product.priceGBP / product.originalPriceGBP) * 100) : null;

  const countdown = useCountdown(product.flashExpiry);

  useEffect(() => {
    if (!discount || !imgSrc || imgSrc.startsWith('data:')) return;
    let cancelled = false;
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = () => {
      try {
        const S = 16;
        const c = document.createElement('canvas');
        c.width = S; c.height = S;
        const ctx = c.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(probe, 0, 0, S, S);
        // bottom-left quadrant - where the badge sits
        const d = ctx.getImageData(0, Math.floor(S * 0.6), Math.ceil(S * 0.5), Math.floor(S * 0.4)).data;
        let sum = 0, n = 0;
        for (let i = 0; i < d.length; i += 4) { sum += (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) / 255; n++; }
        if (!cancelled && n) setBadgeOnDark(sum / n < 0.5);
      } catch { /* cross-origin taint - keep default */ }
    };
    probe.onerror = () => undefined;
    probe.src = imgSrc;
    return () => { cancelled = true; };
  }, [imgSrc, discount]);

  function like(e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    const nowSaved = !saved;
    toggleFav({
      id: product.id, title: product.title, brand: product.brand,
      priceGBP: product.priceGBP, imageUrl: product.imageUrl,
      condition: product.condition, originalPriceGBP: product.originalPriceGBP,
    });
    if (user) {
      if (nowSaved) api.post('/me/watch', { listingId: product.id, saved: true }).catch(() => undefined);
      else api.delete(`/me/watch/${product.id}`).catch(() => undefined);
    }
    toast.success(nowSaved ? 'Saved to your likes.' : 'Removed from your likes.');
  }

  return (
    <Link to={href} className="group block bg-white border border-warmline rounded-2xl overflow-hidden shadow-luxe transition-all duration-500 ease-souq hover:-translate-y-[3px] hover:shadow-glow">
      <div className={clsx(
        'aspect-[4/3] overflow-hidden relative',
        product.imageClass ?? 'img-frame',
      )}>
        {/* Shimmer skeleton until the image decodes - no blank/jumpy tiles. */}
        {showImg && !loaded && <div className="absolute inset-0 skeleton" />}
        {showImg && (
          <img src={imgSrc} alt={`${product.brand ?? ''} ${product.title}`.trim()}
            loading="lazy"
            onLoad={() => setLoaded(true)}
            onError={() => { setLoaded(true); setImgSrc(placeholder(`${product.brand ?? ''} ${product.title}`.trim(), (product as { category?: string }).category ?? '')); }}
            className={clsx(
              'absolute inset-0 w-full h-full object-cover object-[57%_50%] img-cohesive transition-all duration-700 ease-souq group-hover:scale-[1.04]',
              loaded ? 'opacity-100' : 'opacity-0',
            )} />
        )}
        {!showImg && (
          <>
            <div className="absolute inset-0 dotgrid-light bg-[length:14px_14px] opacity-50" />
            <div className="absolute inset-0 grid place-items-center">
              <span className="display text-5xl text-gold/40 italic">
                {(product.brand?.[0] ?? product.title?.[0] ?? 'S').toUpperCase()}
              </span>
            </div>
          </>
        )}

        {/* Like / save - always available, top-right */}
        <button
          type="button"
          onClick={like}
          aria-label={saved ? 'Remove from likes' : 'Save to likes'}
          aria-pressed={saved}
          className={clsx(
            'absolute top-2.5 right-2.5 z-20 grid place-items-center w-9 h-9 rounded-full backdrop-blur-md transition-all duration-200 shadow-sm',
            saved
              ? 'bg-white text-bad scale-100'
              : 'bg-black/25 text-purewhite hover:bg-white hover:text-bad',
          )}
        >
          <HeartIcon filled={saved} />
        </button>

        {countdown && (
          <div className="absolute top-0 left-0 right-0 bg-bad/90 backdrop-blur text-cream text-center py-1.5 stamp text-[9px] z-10">
            FLASH DEAL &middot; ENDS IN {countdown}
          </div>
        )}

        {product.type === 'OFFER' && <span className="pill pill-ink absolute top-3 left-3 text-[9px] z-10">Offers open</span>}
        {product.type === 'FREE'  && <span className="pill bg-sage text-cream absolute top-3 left-3 text-[9px] z-10">Free shelf</span>}
        {product.type === 'SWAP'  && <span className="pill bg-clay text-cream absolute top-3 left-3 text-[9px] z-10">Swappable</span>}
        {product.verified && !product.type && (
          <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 pill bg-gold-soft text-gold-dark text-[9px] font-semibold backdrop-blur-sm">
            <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            Verified
          </span>
        )}
        {discount && (
          <span className={clsx('absolute bottom-3 left-3 stamp px-2.5 py-1 rounded-md text-[9px] font-semibold z-10 shadow-sm backdrop-blur-sm', badgeOnDark ? 'bg-white/92 text-ink' : 'bg-ink/90 text-cream')}>
            {discount}% OFF
          </span>
        )}

        {/* Quick add-to-bag: always-visible round button. A single tap still opens the product (button stops propagation), so there is no touch double-tap. */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault(); e.stopPropagation();
            if (inCart) { navigate('/cart'); return; }
            addToCart({ id: product.id, title: product.title, brand: product.brand, priceGBP: product.priceGBP, imageUrl: product.imageUrl, parcelTier: (product as any).parcelTier });
            toast.success('Added to your bag.');
          }}
          aria-label={inCart ? 'In your bag - view bag' : 'Add to bag'}
          className={clsx(
            'absolute bottom-2.5 right-2.5 z-20 grid place-items-center w-9 h-9 rounded-full shadow-sm backdrop-blur transition-all duration-200 group-hover:scale-105',
            inCart ? 'bg-gold text-ink' : 'bg-ink/90 text-cream hover:bg-gold hover:text-ink',
          )}
        >
          {inCart ? (
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          )}
        </button>
      </div>

      {/* Product meta - on the white card body */}
      <div className="px-3 pt-2.5 pb-3 font-sans">
        {product.brand && <div className="text-[10px] font-semibold uppercase tracking-wide text-ink/65">{product.brand}</div>}
        <div className="text-[13px] leading-snug font-semibold text-ink line-clamp-2 min-h-[2.2em] group-hover:text-gold-dark transition mt-0.5">{product.title}</div>

        {/* Condition rating */}
        <div className="flex items-center gap-1.5 mt-1">
          <span className="flex gap-0.5" aria-label={conditionLabel[product.condition]}>
            {Array.from({ length: 5 }).map((_, i) => (
              <span key={i} className={clsx('w-[6px] h-[6px] rounded-full', i < conditionDots[product.condition] ? 'bg-gold' : 'bg-warmline')} />
            ))}
          </span>
          <span className="text-[10px] text-ink/70">{conditionLabel[product.condition]}{product.size ? ` · ${product.size}` : ''}{product.verified ? ' · ✓ Verified' : ''}</span>
        </div>

        {product.co2SavedKg && (
          <div className="text-[10px] text-sage-dark mt-1">&#x267B; {product.co2SavedKg}kg CO&#x2082; saved by buying pre-loved</div>
        )}
        {/* Price - bold price, strikethrough RRP (the % off lives on the image) */}
        <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5 mt-1.5">
          <span className="text-[17px] font-bold text-ink tracking-tight leading-none">&pound;{product.priceGBP.toFixed(0)}</span>
          {product.originalPriceGBP && product.originalPriceGBP > product.priceGBP && (
            <span className="text-[11px] text-ink/40 line-through">&pound;{product.originalPriceGBP.toFixed(0)}</span>
          )}
        </div>

        {product.sellerHandle && <div className="text-[10px] text-ink/45 mt-1">by {product.sellerHandle}</div>}
      </div>
    </Link>
  );
}
